import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Plus, Pencil, Trash2, X, Wrench, Star, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminServices = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', icon: 'Building2', features: '', featured: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const iconOptions = [
    'Building2', 'Wrench', 'Droplets', 'Zap', 'Shield', 'Paintbrush', 
    'Search', 'Home', 'Plane', 'HardHat', 'CheckCircle', 'Users'
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setServices(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      features: formData.features.split('\n').map(f => f.trim()).filter(f => f)
    };
    
    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/services/${editingItem.id}`
        : `${API_URL}/api/admin/services`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingItem ? 'Serviço atualizado!' : 'Serviço criado!');
        setShowModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este serviço?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/services/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Serviço eliminado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      icon: item.icon,
      features: item.features.join('\n'),
      featured: item.featured
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: '', description: '', icon: 'Building2', features: '', featured: false });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Serviços</h1>
            <p className="text-gray-500">Gerencie os serviços oferecidos</p>
          </div>
          <Button onClick={() => { setShowModal(true); resetForm(); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-2" />
            Novo Serviço
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid gap-4">
            {services.map((item, index) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="text-gray-400">
                        <GripVertical size={18} />
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Wrench className="text-blue-600" size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">{item.title}</h3>
                          {item.featured && <Badge className="bg-orange-500">Destaque</Badge>}
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-1">{item.description}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.features.slice(0, 3).map((f, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                          ))}
                          {item.features.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{item.features.length - 3}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(item)}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {services.length === 0 && (
              <div className="text-center py-12 text-gray-500">Nenhum serviço encontrado</div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500">Total: {services.length} serviços</p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg my-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingItem ? 'Editar Serviço' : 'Novo Serviço'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X size={18} /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nome do Serviço *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                </div>
                <div>
                  <Label>Descrição *</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} required />
                </div>
                <div>
                  <Label>Ícone</Label>
                  <select value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} className="w-full h-10 px-3 border rounded-md">
                    {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Características (uma por linha)</Label>
                  <Textarea 
                    value={formData.features} 
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })} 
                    rows={4} 
                    placeholder="Diagnóstico técnico&#10;Tratamento de fissuras&#10;Pintura especializada"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch 
                    checked={formData.featured} 
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                  />
                  <Label>Serviço em Destaque</Label>
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">{editingItem ? 'Atualizar' : 'Criar'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminServices;
